import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { ExternalLinkIcon } from './external-link-icon';
import { useNewTabLinkProps } from './use-new-tab-link-props';

export type NewTabAnchorProps = Omit<ComponentPropsWithoutRef<'a'>, 'target' | 'rel'> & {
    href: string;
    children: ReactNode;
    /** When set, builds an enriched aria-label. Otherwise a sr-only new-tab hint is appended. */
    accessibleName?: string;
    /** When true, appends the decorative external icon. Defaults to true. */
    showExternalIcon?: boolean;
};

/**
 * Anchor that opens in a new tab with consistent a11y (rel, aria-label or sr-only, optional icon).
 */
export function NewTabAnchor({
    href,
    children,
    accessibleName,
    showExternalIcon = true,
    className,
    ...props
}: NewTabAnchorProps): ReactElement {
    const { linkProps, openInNewTabLabel } = useNewTabLinkProps(accessibleName);

    return (
        <a href={href} className={className} {...props} {...linkProps}>
            {children}
            {!accessibleName ? <span className="sr-only"> ({openInNewTabLabel})</span> : null}
            {showExternalIcon ? <ExternalLinkIcon className="ms-1 inline-block align-text-bottom" /> : null}
        </a>
    );
}
